/**
 * Bosnian strings for the admin screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 *
 * Two clusters of copy in here carry security meaning rather than
 * instructions, and translating them loosely would quietly change what the
 * administrator is being told:
 *
 * - **The QR badge is a bearer credential.** Whoever holds the card — or a
 *   photograph of it — signs in as that person. Every language has to say
 *   that a photograph is enough, not merely that the card should be looked
 *   after, and has to say that issuing a new card kills the old one *at
 *   once* rather than eventually.
 * - **Activation codes are shown exactly once and are not a password.** The
 *   administrator never chooses somebody else's password; they hand over a
 *   one-time code and the worker sets their own. Wording that blurs the two
 *   invites the very habit — one password known to two people — the flow was
 *   built to end.
 */

export const admin = {
  'admin.users.pager.empty': 'Nema korisnika',
  // ---- Shell -------------------------------------------------------
  'admin.title': 'Administracija',
  'admin.subtitle': 'Korisnici, šifarnici, 5S obrasci i dnevnik izmjena',

  'admin.nav.users': 'Korisnici',
  'admin.nav.plants': 'Pogoni',
  'admin.nav.workCenters': 'Radni centri',
  'admin.nav.shifts': 'Smjene',
  'admin.nav.templates': '5S obrasci',
  'admin.nav.badges': 'QR naljepnice',
  'admin.nav.log': 'Dnevnik',

  // ---- Verbs and fields the admin screens share --------------------
  // Kept here rather than in common.ts: these are the words on *these*
  // dialogs, and a screen elsewhere changing its mind about "Odustani" must
  // not silently repaint six admin forms.
  'admin.action.cancel': 'Odustani',
  'admin.action.save': 'Sačuvaj',
  'admin.action.close': 'Zatvori',
  'admin.action.done': 'Gotovo',
  'admin.action.edit.label': 'Izmijeni {name}',
  'admin.action.delete.label': 'Obriši {name}',

  'admin.form.editTitle': 'Izmjena — {name}',
  'admin.saved': 'Izmjene su sačuvane.',
  'admin.delete.failed': 'Brisanje nije uspjelo.',
  'admin.inactive': 'Neaktivan',

  'admin.field.name': 'Naziv',
  'admin.field.code': 'Šifra',
  'admin.field.plant': 'Pogon',
  'admin.field.plant.none': 'Bez pogona',
  'admin.field.sortOrder': 'Redoslijed',
  'admin.field.sortOrder.hint': 'Manji broj se prikazuje prvi.',

  // ---- Users -------------------------------------------------------
  // The two things an administrator has to know before the first account
  // exists, and neither is guessable from the form: what the employee code is
  // for, and that saving does not produce a usable login. Everything else on
  // this screen is recoverable; a code nobody wrote down is not.
  'admin.users.help':
    'Šifra zaposlenog je ono čime se korisnik prijavljuje i ono što piše na njegovoj kartici — po njoj ga aplikacija prepoznaje. Novi nalog stiže neupotrebljiv: uz njega dobijete jednokratni aktivacioni kod koji saopštavate korisniku, a lozinku bira on sam i zna je samo on.',
  'admin.users.search.placeholder': 'Ime, šifra ili e-pošta',
  'admin.users.search.label': 'Pretraga korisnika',
  'admin.users.role': 'Uloga',
  'admin.users.role.all': 'Sve uloge',
  // What each role *does* is the server's sentence, shown under the picker —
  // see the `description` on `UserRole::options()`. This is the part no role
  // description can carry, because it is about all of them at once: three of
  // the five describe a decision about money that KaproMES has no concept of,
  // so `applyMesRole` only ever writes administrator and only ever upwards.
  // Deliberately without the role names: they are enum labels and belong to
  // /api/meta, not to this file.
  'admin.users.role.explain':
    'Uloge koje pregledaju tuđe prijave, ocjenjuju prijedloge i dodjeljuju nagrade postoje samo u KaProLean-u — KaproMES ne zna ko odobrava poboljšanje, pa se te uloge dodjeljuju ovdje i sinhronizacija ih ne dira.',
  'admin.users.badges': 'Kartice',
  'admin.users.new': 'Novi korisnik',

  'admin.users.column.name': 'Ime',
  'admin.users.column.email': 'E-mail',
  'admin.users.column.lastLogin': 'Zadnja prijava',
  'admin.users.column.actions': 'Akcije',

  'admin.users.you': '(vi)',
  // "Nikad" rather than a dash: an account nobody has ever signed into is
  // the most actionable fact on the screen, and an em dash reads as missing
  // data instead of as an answer.
  'admin.users.neverSignedIn': 'Nikad',
  'admin.users.empty.title': 'Nema korisnika',
  'admin.users.empty.description': 'Promijenite pretragu ili dodajte novog.',

  'admin.users.action.edit.tip': 'Izmijeni podatke',
  'admin.users.action.badge.label': 'QR kartica za {name}',
  'admin.users.action.badge.tip.reissue': 'Kartica je izdata — izdaj novu',
  'admin.users.action.badge.tip.issue': 'Izdaj QR karticu',
  'admin.users.action.reset.label': 'Vrati pristup za {name}',
  'admin.users.action.reset.tip': 'Izdaj novi aktivacioni kod',
  'admin.users.action.delete.tip.self': 'Ne možete obrisati vlastiti nalog',
  // "Obriši ili deaktiviraj nalog" stood here and was not true of the button:
  // `destroy` always deletes, and the deactivation it also performs is not an
  // alternative the button chooses between. The tooltip now says what the
  // button costs, which is the thing somebody weighs before pressing it.
  'admin.users.action.delete.tip': 'Obriši nalog — prijave ostaju, ali bez imena podnosioca',

  // The old body said the history stays, which is true and was the whole of
  // it. `Submission::createdBy` is a plain relation to a soft-deleting model,
  // so a deleted user is simply not there any more and every prijava they
  // filed loses its author. Saying only the reassuring half is how somebody
  // deletes an account and finds out a week later.
  'admin.users.delete.confirm':
    'Obrisati korisnika „{name}"?\n\nNjegove prijave, ocjene i evaluacije ostaju — one su istorija pogona — ali više ne pokazuju ko ih je podnio. Ako treba da se zna, umjesto brisanja isključite „Nalog je aktivan": pristup gubi isto tako odmah, a ime ostaje.',
  'admin.users.delete.done': 'Korisnik je obrisan.',

  'admin.users.form.created': 'Korisnik je kreiran.',
  'admin.users.form.noPassword':
    'Lozinku ne postavljate vi. Nakon čuvanja dobićete aktivacioni kod koji saopštavate radniku — on sam bira lozinku pri prvoj prijavi.',

  'admin.users.field.name': 'Ime i prezime',
  'admin.users.field.name.placeholder': 'npr. Marko Marković',
  'admin.users.field.code': 'Šifra zaposlenog',
  'admin.users.field.code.hint': 'Ovim se korisnik prijavljuje u aplikaciju.',
  // What the static hint above cannot say without becoming a paragraph: the
  // uniqueness rule (`Rule::unique` on `employee_code`), and the one
  // consequence of editing it that nothing on this screen shows — the card in
  // the worker's pocket still has the old code printed under the QR, and the
  // QR itself goes on working, so nothing looks broken until somebody reads
  // the card aloud.
  'admin.users.field.code.explain':
    'Mora biti jedinstvena — dvije osobe je ne mogu dijeliti; ako je kasnije promijenite, korisnik se od tada prijavljuje novom šifrom, a ona odštampana na njegovoj kartici više ne odgovara.',
  'admin.users.field.code.placeholder': 'npr. 1042',
  'admin.users.field.email': 'E-pošta',
  'admin.users.field.email.hint': 'Nije obavezna.',
  'admin.users.field.email.placeholder': 'ime.prezime@firma.com',
  'admin.users.field.phone': 'Telefon',
  'admin.users.field.phone.placeholder': 'npr. 063 123 456',
  'admin.users.field.notify': 'Šalji obavještenja i na e-poštu',
  'admin.users.field.notify.hint':
    'Obavještenja u aplikaciji stižu uvijek. Ovo vrijedi samo ako korisnik ima e-poštu.',
  'admin.users.field.active': 'Nalog je aktivan',
  // The half that was missing is what makes this the answer to "obrisati ili
  // deaktivirati": `update()` drops the user's tokens the moment the box is
  // cleared, so access goes at once — and the account stays on the list, which
  // is why the prijave it left behind keep naming their author. A soft-deleted
  // user is not loaded by `createdBy`, so deleting takes the name off them.
  'admin.users.field.active.hint':
    'Neaktivan korisnik se ne može prijaviti i sve njegove otvorene sesije prestaju odmah, ali ostaje na spisku i njegove prijave i dalje nose njegovo ime.',

  // Shown exactly once, on the response that created the account. "Prikazuje
  // se samo sada" is the whole point of the sentence and every language has
  // to keep it — the code cannot be read back out of anywhere.
  'admin.users.activation.intro':
    'Saopštite radniku ovaj aktivacioni kod. Prikazuje se samo sada — ako se izgubi, izdajte novi.',
  'admin.users.activation.note':
    'Njime se prijavljuje jednom, a zatim sam postavlja lozinku koju zna samo on.',
  'admin.users.code.copy': 'Kopiraj',
  'admin.users.code.copied': 'Kod je kopiran.',

  'admin.users.reset.title': 'Vraćanje pristupa — {name}',
  'admin.users.reset.issue': 'Izdaj aktivacioni kod',
  // Beside the button, because the three paragraphs above it say what the code
  // is for and none of them says what the account can do in the meantime. It
  // can do nothing: `issueActivationCode()` sets `must_set_password`, and
  // `EnsurePasswordIsSet` refuses every route but the one that sets it.
  'admin.users.reset.explain':
    'Kod vrijedi za jednu prijavu i nalog do tada ne može ništa drugo — korisnik se njime prijavi i odmah postavi svoju lozinku.',
  'admin.users.reset.why':
    'Radnici najčešće nemaju e-poštu, pa je ovo način da im vratite pristup.',
  'admin.users.reset.note':
    'Ne birate lozinku umjesto njih — dobijate kod za jednokratnu prijavu, a lozinku postavlja radnik.',
  'admin.users.reset.warning': 'Sve aktivne prijave tog korisnika biće prekinute.',
  'admin.users.reset.codeIntro': 'Saopštite radniku ovaj kod. Prikazuje se samo sada.',
  'admin.users.reset.codeNote':
    'Njime se prijavljuje jednom, a zatim sam postavlja lozinku koju zna samo on. Sve ranije prijave tog korisnika su prekinute.',
  'admin.users.issue.failed': 'Izdavanje nije uspjelo.',

  'admin.users.badge.title': 'QR kartica — {name}',
  'admin.users.badge.print': 'Štampaj karticu',
  'admin.users.badge.reissue': 'Izdaj novu karticu',
  'admin.users.badge.issue': 'Izdaj karticu',
  // The badge sheet says this and this dialog did not, which is the wrong way
  // round — the sheet is a print run, this is the screen somebody opens
  // because one card was lost. `issueBadgeToken()` overwrites the one token
  // the user has, so reissuing *is* the revocation, and that is worth saying
  // as a capability rather than only as a warning.
  'admin.users.badge.explain':
    'Svaki korisnik ima tačno jednu važeću karticu, pa izdavanje nove poništava onu koju nosi u džepu, odmah — tako se i opoziva izgubljena kartica.',
  'admin.users.badge.why':
    'Radnik skenira karticu umjesto da kuca šifru i lozinku — brže kad se stoji pored mašine, sa rukavicama.',
  'admin.users.badge.printHint':
    'Odštampajte i predajte radniku. Skeniranjem kartice telefonom otvara se aplikacija i prijava je gotova.',
  // The card is a bearer credential. This says so plainly, and says what
  // follows from it: a photograph of the card signs in just as well as the
  // card. An accepted trade on the floor, a poor one for an account that can
  // reset everybody else's.
  'admin.users.badge.managerWarning':
    'Ovo nije radnički nalog. Kartica je nosilac pristupa — fotografija kartice je dovoljna za prijavu. Za rukovodioce i administratore uključite i dvofaktorsku zaštitu.',
  'admin.users.badge.alreadyIssued': 'Kartica je već izdata.',
  'admin.users.badge.alreadyIssuedAt': 'Kartica je već izdata — {when}.',
  'admin.users.badge.revoke': 'Poništi',
  'admin.users.badge.revoke.failed': 'Poništavanje nije uspjelo.',

  // ---- Work centres ------------------------------------------------
  'admin.workCenters.new': 'Novi radni centar',
  'admin.workCenters.noPlants.title': 'Prvo dodajte pogon',
  'admin.workCenters.noPlants.description':
    'Svaki radni centar pripada jednom pogonu, a nijedan još nije kreiran.',
  'admin.workCenters.noPlants.action': 'Otvori pogone',
  // Two sentences rather than "{count}" plus a noun glued on: the noun after
  // a number inflects, and the glued form cannot be repaired in translation.
  'admin.workCenters.count.one': '{count} radni centar',
  'admin.workCenters.count.many': '{count} radnih centara',
  // The type itself comes from the server — this is only the word in front
  // of it.
  'admin.workCenters.typeLabel': 'tip: {type}',
  'admin.workCenters.noQr': 'Bez QR koda',
  'admin.workCenters.noQr.explain':
    'Mašina još nema QR kod, pa je radnik ne može skenirati — može je samo izabrati iz liste. Kod se pravi dugmetom „Obnovi" u „QR naljepnice", a tamo se i štampa.',
  'admin.workCenters.formLabel': 'obrazac: {name}',
  'admin.workCenters.noForm': 'Bez 5S obrasca',
  'admin.workCenters.noForm.explain':
    'Ovaj radni centar se ne može 5S ocjenjivati jer za tip „{type}" ne postoji aktivan obrazac. Napravite obrazac s tim tipom u „5S obrasci".',
  'admin.workCenters.action.remove.label': 'Ukloni {name}',
  'admin.workCenters.empty': 'Nema radnih centara',
  // Only the question. The consequence used to be written here as well, and
  // said the opposite of what the controller now does — `destroy` purges the
  // submissions and evaluations instead of deactivating the centre. It is
  // spelled out once, in `delete.warning`, which is the dialog's body.
  'admin.workCenters.delete.confirm': 'Obrisati radni centar „{name}"?',
  'admin.workCenters.created': 'Radni centar je kreiran. Odštampajte QR naljepnicu.',

  // Both preconditions for a new work centre. The plant has its own empty
  // state; this is the other one, and the only thing on the screen that says
  // the 5S obrazac has to exist first.
  'admin.workCenters.noTemplates':
    'Novi radni centar se ne može kreirati dok ne postoji aktivan 5S obrazac za njegov tip — tip se bira iz liste obrazaca, a nijedan još nije napravljen. Otvorite „5S obrasci" i napravite prvi.',

  'admin.workCenters.field.name.placeholder': 'npr. Hoblanje',
  'admin.workCenters.field.code.hint':
    'Velika slova, brojevi, crtica. Koristi se u URL-ovima i na naljepnici.',
  'admin.workCenters.field.code.placeholder': 'npr. HOB-01',
  'admin.workCenters.field.type': '5S obrazac',
  // The field names the checklist rather than the type, because the checklist
  // is what people remember: Livprom looked for "obrada stranica 2" in a list
  // of type codes and never recognised `LIGNUM 2`. The hint says where to go
  // when the checklist does not exist yet.
  'admin.workCenters.field.type.hint':
    'Radni centar se ocjenjuje po obrascu koji ovdje izaberete. Ako obrasca nema u listi, prvo ga napravite u „5S obrasci".',
  'admin.workCenters.field.type.explain':
    'Isti obrazac može koristiti više radnih centara, a izmjena obrasca vrijedi za sve njih. Ako ovom mjestu treba vlastita lista kriterijuma, napravite mu novi obrazac u „5S obrasci" i izaberite ga ovdje.',
  'admin.workCenters.field.type.placeholder': 'Izaberite 5S obrazac',
  'admin.workCenters.field.type.noMatch':
    'Nema obrasca s tim nazivom ili tipom. Novi obrazac se pravi u „5S obrasci".',
  'admin.workCenters.field.type.unused': 'još ga ne koristi nijedan radni centar',
  'admin.workCenters.field.type.orphan':
    'Tip „{type}" nema aktivan 5S obrazac, pa se ovaj radni centar trenutno ne može ocjenjivati.',
  'admin.workCenters.field.type.ownForm':
    'Ovaj radni centar ima vlastiti obrazac „{name}" i ocjenjuje se po njemu, bez obzira na izbor ovdje.',
  'admin.workCenters.field.description': 'Opis',
  'admin.workCenters.field.description.placeholder':
    'Neobavezno — čemu centar služi, gdje se nalazi u pogonu.',
  'admin.workCenters.field.active': 'Radni centar je aktivan',
  'admin.workCenters.field.active.hint':
    'Neaktivan centar se ne nudi pri prijavi niti pri 5S evaluaciji.',

  // ---- Plants ------------------------------------------------------

  // ---- Shifts ------------------------------------------------------
  // Everything this used to carry now sits in `admin.shifts.help`, which is
  // directly underneath it. Two blocks of the same sentence, one grey and one
  // in a dashed box, read as a screen that does not know what it wants to say.
  'admin.shifts.subtitle': 'Smjene u kojima se radi.',
  // The three things somebody wonders before touching this screen: whether
  // they have to, what they get if they do, and whether the night shift is
  // going to be refused.
  'admin.shifts.help':
    'Smjene su neobavezne: ako ih ne unesete, polje za smjenu se uopšte ne prikazuje na 5S obrascu. Unesite ih ako želite da poredite kako se radni centri održavaju iz smjene u smjenu — evaluator tada bira smjenu pri svakom obilasku. Noćna smjena se završava prije nego što počne i tako se i upisuje.',
  'admin.shifts.new': 'Nova smjena',
  'admin.shifts.edit': 'Izmijeni',
  // The empty state has to say what filling this in buys, or a screen that is
  // deliberately empty reads as one that is broken.
  'admin.shifts.empty.title': 'Nema definisanih smjena',
  'admin.shifts.empty.description':
    'Dodajte prvu smjenu, ili ostavite prazno ako se u pogonu radi u jednoj smjeni.',
  'admin.shifts.noWindow': 'Bez vremenskog okvira',
  'admin.shifts.walks': '{count} evaluacija',
  'admin.shifts.created': 'Smjena je kreirana.',
  'admin.shifts.updated': 'Smjena je izmijenjena.',
  'admin.shifts.form.description': 'Naziv je obavezan; vrijeme nije.',
  'admin.shifts.field.startsAt': 'Početak',
  'admin.shifts.field.endsAt': 'Kraj',
  // Said once, on the field people expect to be refused.
  'admin.shifts.field.window.hint': 'Noćna smjena se završava prije nego počne — to je u redu.',
  // What the clock window does, and — more usefully — what it does not. It is
  // read by `Shift::covers()` to preselect a shift on the walk form and by
  // nothing else; it gates nobody's evaluation.
  'admin.shifts.field.window.explain':
    'Vrijeme služi samo da obrazac sam ponudi smjenu koja traje u trenutku obilaska; evaluator tu ponudu mijenja jednim dodirom, a smjena bez vremena se bira ručno.',
  'admin.shifts.field.sortOrder.hint': 'Redoslijed kojim se radi, ne abecedni.',
  'admin.shifts.field.active': 'Aktivna',
  'admin.shifts.field.active.hint':
    'Neaktivna smjena se ne nudi na obrascu, ali ostaje na ranijim evaluacijama.',
  'admin.shifts.delete.confirm': 'Obrisati smjenu „{name}"?',
  'admin.shifts.delete.willDelete': 'Smjena se nigdje ne koristi, pa se briše trajno.',
  'admin.shifts.delete.willDeactivate':
    'Smjena se koristi u {count} evaluacija, pa se neće obrisati nego deaktivirati. Ranije evaluacije zadržavaju njen naziv.',
  'admin.shifts.delete.action': 'Nastavi',

  'admin.plants.subtitle': 'Lokacije firme. Svaki radni centar pripada tačno jednom pogonu.',
  // Where a pogon sits, and what deleting one really costs.
  //
  // The second half used to be the opposite sentence — "pogon sa radnim
  // centrima se ne može obrisati" — and it was true of the controller until
  // `destroy` started calling `PurgeWorkCenter::purgePlant`. An instruction
  // that promises a refusal which no longer happens is worse than none: it is
  // read as a safety net by the one person standing over the button.
  'admin.plants.help':
    'Hijerarhija je firma → pogon → radni centar: svaka prijava i svaka 5S evaluacija visi o radnom centru, a svaki radni centar o tačno jednom pogonu. Zato pogon postoji prije radnih centara koje drži. Brisanje pogona nije uklanjanje jedne lokacije — sa njim trajno odlaze i svi radni centri u njemu, zajedno sa svime što je na njima prijavljeno i ocijenjeno.',
  'admin.plants.new': 'Novi pogon',
  'admin.plants.empty.title': 'Nema pogona',
  'admin.plants.empty.description': 'Dodajte prvi pogon da biste mogli kreirati radne centre.',
  'admin.plants.workCenterCount': 'Radnih centara',
  'admin.plants.userCount': 'Korisnika',
  'admin.plants.edit': 'Uredi',
  'admin.plants.delete.tip': 'Obriši pogon',
  'admin.plants.deleted': 'Pogon je obrisan.',
  'admin.plants.created': 'Pogon je kreiran.',
  'admin.plants.updated': 'Pogon je izmijenjen.',
  'admin.plants.form.description': 'Lokacija firme na kojoj se nalaze radni centri.',
  'admin.plants.field.code.hint':
    'Velika slova i brojevi. Pojavljuje se u izvještajima i na naljepnicama.',
  'admin.plants.field.address': 'Adresa',

  // ---- 5S templates ------------------------------------------------
  // The answer to the question the customer actually asked — "can we enter a
  // new work place ourselves?" — plus the order that answer depends on. The
  // last sentence is `guardTemplateExists`, said before it fires rather than
  // as a rejected save.
  'admin.templates.help':
    'Obrasci su vaši — nova radna mjesta i nove kriterijume unosite sami, bez nas. Redoslijed nije proizvoljan: prvo obrazac (naziv, tip radnog centra i skala), zatim pet kategorija, pa kriterijumi u njima, i tek onda radni centar tog tipa. Radni centar se ne može sačuvati dok njegov tip nema aktivan obrazac.',
  'admin.templates.empty': 'Nema 5S obrazaca',
  'admin.templates.meta':
    'tip: {type} · skala 1–{scale} · {items} kriterijuma · {evaluations} evaluacija',
  'admin.templates.stats': '{items} kriterijuma · {evaluations} evaluacija',
  'admin.templates.locked': 'Zaključan',
  'admin.templates.newVersion': 'Nova verzija',
  // Beside the button, because the button is offered on every checklist and
  // the two words promise nothing about the evaluations already filed.
  'admin.templates.newVersion.hint':
    'Pravi kopiju obrasca kao sljedeću verziju i arhivira ovu — ranije evaluacije ostaju vezane za nju i njihove ocjene se ne mijenjaju, a nove se rade po novoj verziji.',
  // In the place the "Nova kategorija" button occupies on an unlocked
  // checklist. A control that is simply absent teaches nothing.
  //
  // The middle sentence is new and the whole reason this string changed: the
  // bin beside a category is now offered on a locked checklist too, so a note
  // saying categories cannot be removed would be flatly contradicted by a
  // button on the same screen.
  'admin.templates.locked.noAdd':
    'Kategorije i kriterijumi se ne mogu dodavati jer su ranije evaluacije ocijenjene po ovom obrascu. Kategoriju po kojoj niko nikada nije ocjenjivan možete obrisati — nema ocjene koju bi to promijenilo. Sve ostalo ide kroz „Nova verzija" na vrhu kartice.',
  // The freeze exists so a past score keeps meaning what it meant when it
  // was given. Say why, not only that it is refused.
  'admin.templates.lockedNote':
    'Obrazac je korišten za predate evaluacije, pa se tekst kriterijuma i skala više ne mogu mijenjati — izmjena bi retroaktivno promijenila značenje ranijih ocjena. Da biste ga mijenjali, napravite novu verziju.',
  'admin.templates.newVersion.confirm':
    'Napraviti verziju {next} obrasca „{name}"?\n\nVerzija {current} se arhivira, ali ranije evaluacije ostaju vezane za nju i njihove ocjene se ne mijenjaju. Nove evaluacije koriste novu verziju.',
  'admin.templates.newVersion.failed': 'Kreiranje verzije nije uspjelo.',
  'admin.templates.archived.title': 'Arhivirane verzije',
  'admin.templates.archived.note':
    'Ranije evaluacije su vezane za ove verzije i njihove ocjene ostaju nepromijenjene.',

  'admin.templates.criterion': 'Kriterijum',
  'admin.templates.criterion.edit': 'Izmijeni',
  'admin.templates.criterion.edit.label': 'Izmijeni: {criterion}',
  'admin.templates.criterion.remove.label': 'Ukloni: {criterion}',
  // Why a removed criterion is still on the list, struck through. `destroyItem`
  // deletes outright only when nothing has ever been scored against it.
  'admin.templates.criterion.remove.hint':
    'Kriterijum po kojem je već ocjenjivano se ne briše nego se deaktivira — ostaje ovdje precrtan da bi ranije evaluacije i dalje bile čitljive.',
  'admin.templates.criterion.delete.confirm': 'Ukloniti kriterijum „{criterion}"?',
  'admin.templates.criterion.editTitle': 'Izmjena kriterijuma',
  'admin.templates.criterion.newTitle': 'Novi kriterijum',
  'admin.templates.criterion.hint':
    'Tekst koji ocjenjivač čita na terenu — treba da odgovara štampanom obrascu.',
  'admin.templates.criterion.placeholder':
    'npr. Nema nepotrebne opreme, alata i materijala na radnom mjestu',
  'admin.templates.criterion.saved': 'Sačuvano.',

  'admin.templates.guidance': 'Uputstvo',
  'admin.templates.guidance.hint':
    'Neobavezna pomoć ocjenjivaču. Može se mijenjati i na zaključanom obrascu.',
  'admin.templates.guidance.placeholder': 'npr. Provjeriti i ispod stola i iza mašine.',

  'admin.templates.category.add': 'Nova kategorija',
  'admin.templates.category.submit': 'Dodaj',
  'admin.templates.category.name.hint': 'Npr. Sortiranje, Uređivanje, Čistoća.',
  'admin.templates.category.name.placeholder': 'npr. Sortiranje',
  'admin.templates.category.added': 'Kategorija je dodata.',
  'admin.templates.category.failed': 'Dodavanje nije uspjelo.',
  'admin.templates.category.delete.label': 'Obriši kategoriju: {name}',
  'admin.templates.category.delete.confirm': 'Obrisati kategoriju „{name}"?',
  // The count, because "sa svim kriterijumima" is a sentence somebody clicks
  // past and "sa 15 kriterijuma" is one they weigh. The second half is the
  // rule the server applies — it refuses a category po kojoj je ocjenjivano,
  // zaključan obrazac ili ne.
  'admin.templates.category.delete.warning':
    'Briše se zajedno sa kriterijumima u njoj ({count}). Ako je po nekom od njih već ocjenjivano, brisanje se odbija — te ocjene bi ostale bez kriterijuma koji objašnjava šta znače.',

  // ---- A checklist for one machine ---------------------------------
  // The customer's question: „može li se napraviti veza sa radnim centrom".
  // Obrazac inače pokriva tip mašine, što je tačno za pet istih hoblarica i
  // netačno za jednu koja traži svoje kriterijume.
  'admin.templates.settings': 'Izmijeni obrazac',
  'admin.templates.settings.saved': 'Obrazac je sačuvan.',
  'admin.templates.meta.machine':
    'obrazac za jednu mašinu · skala 1–{scale} · {items} kriterijuma · {evaluations} evaluacija',
  'admin.templates.machine.badge': 'Jedna mašina',
  'admin.templates.machine.carveOut': 'Poseban obrazac za mašinu',
  'admin.templates.machine.carveOut.submit': 'Napravi',
  'admin.templates.machine.carveOut.hint':
    'Pravi kopiju ovog obrasca samo za jednu mašinu — original i ostale mašine istog tipa ostaju nepromijenjeni, a kopiju odmah možete urediti.',
  'admin.templates.machine.carveOut.none':
    'Nijedan aktivan radni centar ne koristi ovaj tip, pa nema mašine koju bi bilo šta izdvojilo.',
  'admin.templates.machine.carveOut.note':
    'Mašina se od sada ocjenjuje po svojoj kopiji, a ne po obrascu tipa. Ostale mašine tog tipa se ne mijenjaju. Kad god poželite, možete je vratiti na obrazac tipa.',
  'admin.templates.machine.field': 'Radni centar',
  'admin.templates.machine.field.placeholder': 'Odaberite radni centar…',
  'admin.templates.machine.field.hint':
    'Ponuđene su mašine koje ovaj obrazac sada pokriva — izdvajate jednu od njih.',
  'admin.templates.machine.detach': 'Vrati na obrazac tipa',
  'admin.templates.machine.detach.hint':
    'Mašina se ponovo ocjenjuje po obrascu svog tipa; ako je po ovoj kopiji već ocjenjivano, ona se arhivira umjesto da se obriše, da ranije evaluacije ostanu čitljive.',
  'admin.templates.machine.detach.confirm': 'Vratiti „{name}" na obrazac tipa?',
  'admin.templates.machine.detach.warning':
    'Kriterijumi napisani samo za ovu mašinu se više neće koristiti. Ako tip te mašine nema aktivan obrazac, mašina se do daljnjeg neće moći evaluirati — dobićete upozorenje ako je tako.',

  // ---- Badge sheet -------------------------------------------------
  'admin.badgeSheet.title': 'Kartice za štampu',
  'admin.badgeSheet.print': 'Štampaj ({count})',
  'admin.badgeSheet.issue': 'Izdaj kartice ({count})',
  'admin.badgeSheet.intro':
    'Izaberite radnike. Svako dobija svoju karticu koju skenira umjesto da kuca šifru i lozinku.',
  'admin.badgeSheet.printHint':
    'Odštampajte na A4, isjecite po isprekidanoj liniji i plastificirajte. Kartice se prikazuju samo sada — kad zatvorite prozor, nema ih više.',
  'admin.badgeSheet.empty.title': 'Nema radnika',
  'admin.badgeSheet.empty.description': 'Kartice se izdaju samo radnicima, a nijedan još nije dodat.',
  'admin.badgeSheet.item.inactive': 'neaktivan',
  'admin.badgeSheet.item.hasBadge': 'već ima karticu',
  'admin.badgeSheet.truncated':
    'Prikazano je prvih {shown} od {total}. Ostale izdajte iz liste korisnika.',
  // Reissuing kills the card in somebody's pocket the moment it happens.
  // The warning and the confirmation both have to say "odmah" — an
  // administrator who expects a grace period sends a shift home cardless.
  'admin.badgeSheet.reissue.one': 'Jedan izabrani radnik već ima karticu — stara prestaje da važi.',
  'admin.badgeSheet.reissue.many':
    '{count} izabranih radnika već imaju kartice — stare prestaju da važe.',
  'admin.badgeSheet.reissue.confirm.one':
    '{count} radnik već ima karticu.\n\nIzdavanjem nove, kartica koju sada nosi prestaje da važi odmah. Moraćete mu predati novu.',
  'admin.badgeSheet.reissue.confirm.many':
    '{count} radnika već imaju karticu.\n\nIzdavanjem nove, kartice koje sada nose prestaju da važe odmah. Moraćete im predati nove.',
  // The server's message and the tally in one template: a run that stopped
  // halfway leaves live, unprinted cards, and the count is what says how
  // many.
  'admin.badgeSheet.partial': '{message} Izdato: {done} od {total}.',
  'admin.badgeSheet.failed': 'Izdavanje nije uspjelo do kraja.',

  // ---- Activation codes for a whole shift --------------------------
  //
  // The sibling of the badge sheet, and the copy carries one thing the badge
  // copy does not have to: a card can be reprinted from the user list, a code
  // cannot be recovered from anywhere. `issueActivationCode()` hashes it on the
  // way into the row exactly as it does a password, so „prikazuju se samo sada"
  // is the literal truth and every language has to keep it.
  //
  // The screen exists because of the nightly KaproMES sync: it creates accounts
  // at 03:20, issues each a code and hands it to a process nobody is watching.
  'admin.activation.action': 'Aktivacioni kodovi',
  'admin.activation.title': 'Aktivacioni kodovi za štampu',
  // „Nemaju svoju lozinku", not „nisu se nikad prijavili". The two are not the
  // same set: `must_set_password` also covers somebody whose access an
  // administrator took back from the user list, and they have signed in
  // plenty. The sentence has to be true of both.
  'admin.activation.intro':
    'Ovi korisnici još nemaju svoju lozinku. Svako dobija jednokratni kod kojim se prijavljuje i odmah postavlja lozinku koju zna samo on.',
  'admin.activation.issue': 'Izdaj kodove ({count})',
  'admin.activation.print': 'Štampaj ({count})',
  // „Ekran" rather than „prozor", because the same sentence sits under the
  // codes on the MES integration page too, and that one is not a dialog.
  'admin.activation.printHint':
    'Odštampajte na A4, isjecite po isprekidanoj liniji i predajte svakom njegov listić. Kodovi se prikazuju samo sada — kad napustite ovaj ekran, nema ih više.',
  // Nobody waiting is the good outcome, so it is said as one rather than as an
  // empty list.
  'admin.activation.empty.title': 'Svi imaju svoju lozinku',
  'admin.activation.empty.description':
    'Nijedan aktivan nalog ne čeka aktivacioni kod. Kad neko izgubi lozinku, kod mu izdajete pojedinačno iz liste korisnika.',
  // The list is a preview and the action is not limited to it — the request
  // carries no names at all, the server issues to everybody waiting.
  'admin.activation.truncated':
    'Prikazano je prvih {shown} od {total}. Kod se izdaje svima koji čekaju, ne samo prikazanima.',
  // Said before the button, not after. Somebody may be holding a slip from
  // Friday that they have not used yet; this run kills it, which is the right
  // trade and a surprise if nobody says so.
  'admin.activation.reissue':
    'Ako je nekom već izdat kod koji još nije iskoristio, taj kod prestaje da važi odmah — predajte mu novi listić.',
  // The whole reason the screen is built around printing.
  'admin.activation.warning':
    'Kodovi se čuvaju šifrovani, pa se poslije ovoga ne mogu nigdje pročitati. Odštampajte ih ili prepišite prije nego napustite ekran; ako se izgube, izdajte nove.',
  // On the printed slip, which leaves the room — whoever hands it over may not
  // be there when it is used. Addressed to the worker, unlike everything else
  // on this screen.
  'admin.activation.slip.employeeCode': 'Šifra zaposlenog',
  'admin.activation.slip.code': 'Aktivacioni kod',
  'admin.activation.slip.instructions':
    'Prijavite se šifrom zaposlenog i ovim kodom, a zatim postavite svoju lozinku — kod poslije toga više ne vrijedi.',

  // ---- QR stickers on the machines ---------------------------------
  // The rest of that screen's copy lives under `fiveS.stickers.*`, which is
  // where it belongs — the page is shared with the 5S walk's vocabulary. This
  // one key is here because it is an administration rule rather than a
  // description of a sticker, and because the confirmation dialog says the
  // same thing a beat too late: by then the administrator has already decided.
  'admin.stickers.help':
    'Obnavljanje koda poništava svaku naljepnicu koja je već na toj mašini: stari kod od tog trenutka ne vodi nigdje, pa radnik koji ga skenira dobija poruku da kod nije poznat i mora sam birati radni centar. Obnovite kod tek kad je naljepnica oštećena ili kad stari kod ne smije više biti u opticaju, i odmah odštampajte i zalijepite novu.',

  // ---- Activity log ------------------------------------------------
  // What the log holds and what it does not, above the filters.
  //
  // The second sentence is the one people actually arrive with. A screen
  // called "Dnevnik" invites the question "can I see who looked at this", and
  // the honest answer is no — nothing records a read, so an administrator who
  // assumes otherwise is relying on a trail that was never written.
  'admin.log.help':
    'Dnevnik sam bilježi izmjene na prijavama i evaluacijama, polje po polje, sa starom i novom vrijednošću; uz to administratorski ekrani upisuju i ono što nije izmjena zapisa — izdat aktivacioni kod, izdatu ili poništenu karticu, novu verziju obrasca, obnovljen QR kod, promijenjenu valutu. Čitanje se ne bilježi: dnevnik odgovara na pitanje ko je šta promijenio, a ne ko je šta vidio.',
  'admin.log.search.placeholder': 'Pretraga po opisu',
  'admin.log.search.label': 'Pretraga dnevnika',
  'admin.log.clearFilters': 'Poništi ({count})',
  'admin.log.filter.subject': 'Šta je mijenjano',
  'admin.log.filter.subject.all': 'Sve stavke',
  'admin.log.filter.event': 'Vrsta izmjene',
  'admin.log.filter.event.all': 'Sve izmjene',
  'admin.log.filter.causer': 'Ko je mijenjao',
  'admin.log.filter.causer.all': 'Svi korisnici',
  'admin.log.filter.logName': 'Vrsta zapisa',
  'admin.log.filter.logName.all': 'Sve vrste',
  'admin.log.filter.from': 'Od datuma',
  'admin.log.filter.to': 'Do datuma',
  'admin.log.empty.title': 'Nema zapisa',
  'admin.log.empty.description':
    'Dnevnik se puni kada korisnici mijenjaju prijave, evaluacije i šifarnike.',
  'admin.log.subject.fallback': 'Zapis',
  'admin.log.causer': '{name} ({code})',
  'admin.log.causer.system': 'Sistem',
  'admin.log.impersonated': 'Podrška — {name}',
  'admin.log.change.arrow': 'promijenjeno u',
  // `1–25 od 63`, in one template. Written as one string rather than a range
  // with "od" between it in JSX, because the connector and what sits either
  // side of it are exactly what moves between languages.
  'admin.log.pager.range': '{from}–{to} od {total}',
  'admin.log.pager.perPage.label': 'Zapisa po stranici',
  'admin.log.pager.perPage': '{count} po stranici',
  'admin.log.pager.prev': 'Prethodna stranica',
  'admin.log.pager.next': 'Sljedeća stranica',

  // Rows the MES sync owns. See SyncedFromBadge for why the reason is
  // spelled out rather than the field merely being greyed.
  'admin.synced.tooltip': 'Ovaj zapis održava {system}. Izmjene se rade tamo.',
  'admin.synced.lastSyncedAt': 'Posljednja sinhronizacija: {at}',
  'admin.synced.fieldLocked': 'Održava {system} — sljedeća sinhronizacija bi vratila izmjenu.',

  // The defect list above the templates — see pages/admin/TemplateGaps.tsx.
  'admin.templates.gaps.title': 'Radni centri bez 5S obrasca',
  'admin.templates.gaps.body': '{types} tipova radnih centara nema aktivan obrazac — {machines} mašina se ne može evaluirati.',
  'admin.templates.gaps.machines': '{count} mašina',
  'admin.templates.gaps.source': 'Obrazac koji se primjenjuje',
  'admin.templates.gaps.source.placeholder': 'Odaberite obrazac…',
  'admin.templates.gaps.apply': 'Primijeni na {count} tipova',
  'admin.templates.gaps.skipped': 'Preskočeno (već imaju obrazac): {types}',
  'admin.templates.gaps.note': 'Kopija je zaseban obrazac — original ostaje aktivan i nepromijenjen. Kriterije možete urediti poslije.',
  // The counts in `gaps.body` say what is wrong; this says what it costs on the
  // floor and what the controls underneath are for.
  'admin.templates.gaps.help':
    'Radnik koji stane pred ove mašine ne može uraditi 5S obilazak: njihov tip nema aktivan obrazac, pa aplikacija nema šta da mu prikaže. Označite tipove, izaberite postojeći obrazac koji im najviše odgovara i primijenite ga — ili napravite novi obrazac za svaki tip.',
  'admin.templates.gaps.apply.hint':
    'Kopira sve kategorije i kriterijume izabranog obrasca na svaki označeni tip; tip koji već ima aktivan obrazac se preskače, a ne prepisuje.',
  'admin.templates.search.placeholder': 'Traži po obrascu, tipu ili mašini…',
  'admin.templates.search.label': 'Pretraga obrazaca',
  'admin.templates.search.none': 'Nema obrasca koji odgovara „{term}".',
  'admin.templates.moreCentres': '+{count} još',
  'admin.templates.noCentres': 'Nijedan radni centar se još ne ocjenjuje po ovom obrascu.',
  'admin.templates.addCentre': 'Dodaj radni centar',
  'admin.templates.addCentre.hint':
    'Otvara formu za novi radni centar s ovim obrascem već izabranim. Postojeći radni centar prebacujete na ovaj obrazac tako što ga uredite u „Radni centri" — osim radnih centara preuzetih iz KaproMES-a, čiji se tip mijenja u KaproMES-u.',
  'admin.nav.integration': 'Integracija',
  'admin.nav.settings': 'Postavke',
  'admin.mes.title': 'MES integracija',
  'admin.mes.subtitle': 'Povežite KaProLean sa vašim MES sistemom — pogoni, radni centri i radnici se preuzimaju svake noći.',
  // The three rules of the sync that decide what an administrator may expect,
  // all read out of SyncFromMes: one direction, never deletes, no password
  // hashes. The middle one is the one worth the words — "deaktivira se" is a
  // reassurance only if somebody knows it is not "briše se".
  'admin.mes.help':
    'Podaci idu samo u jednom smjeru: KaproMES je vlasnik pogona, radnih centara i ljudi, pa se polja koja on drži ovdje ne mogu mijenjati — izmjena bi ostala do noćne sinhronizacije i onda nestala. Sinhronizacija ništa ne briše: radni centar ili osoba koja se prestane pojavljivati u odgovoru se deaktivira, jer je izostanak iz odgovora mnogo češće greška u vezi ili filter nego mašina koja je zaista otpisana. Preuzeta osoba se ne može prijaviti dok sama ne postavi lozinku — lozinke se ne preuzimaju i ne tražimo ih.',
  // Roles, beside the title. `applyMesRole` maps exactly one value and only
  // ever promotes; the asymmetry is the interesting half, and its reason is
  // the fact an administrator can check for themselves — their own system has
  // no id_uloga meaning "smije dodijeliti nagradu".
  'admin.mes.roles.explain':
    'Preko sinhronizacije prelazi samo administratorska uloga i samo naviše: ko je administrator u KaproMES-u postaje administrator i ovdje pri sljedećem preuzimanju, a gubitak te uloge tamo je ne oduzima ovdje — uloge koje odlučuju o prijedlozima i nagradama KaproMES ne poznaje, pa bi ih spuštanje svake noći brisalo.',
  // What the run tells you afterwards, beside the subtitle that promises it
  // runs. Both cases end up in the same `problems` list the report renders.
  'admin.mes.report.explain':
    'Izvještaj poslije preuzimanja nabraja i podignute administratore i uloge iz KaproMES-a koje ne prepoznajemo — takav nalog se i dalje sinhronizuje, samo mu se uloga ne dira.',
  'admin.mes.sync.plants': 'Pogoni',
  'admin.mes.sync.workCenters': 'Radni centri',
  'admin.mes.sync.workers': 'Radnici',
  'admin.mes.sync.deactivated': 'Deaktivirano',
  'admin.mes.sync.adopted': 'Preuzeto postojećih',
  // Above the problems rather than among them: a machine with no checklist
  // keeps until tomorrow, and these do not.
  'admin.mes.sync.activations':
    'Sinhronizacija je otvorila {count} novih naloga i svakom izdala aktivacioni kod.',
  'admin.mes.sync.clean': 'Sinhronizacija je prošla bez problema.',
  'admin.mes.sync.problems': 'Sinhronizacija je prošla, ali treba obratiti pažnju na sljedeće ({count}):',
  'admin.workCenters.search': 'Traži po nazivu, šifri, tipu ili obrascu…',
  'admin.plants.search': 'Traži po nazivu, šifri ili adresi…',
  'admin.workCenters.delete.warning': 'Trajno se brišu i sve prijave, 5S evaluacije i prilozi vezani za ovaj radni centar. Ovo se ne može vratiti.',
  'admin.workCenters.delete.action': 'Obriši sve',
  'admin.templates.new': 'Novi 5S obrazac',
  'admin.templates.new.done': 'Obrazac je kreiran. Sada dodajte kategorije i kriterije.',
  'admin.templates.field.type': 'Tip radnog centra',
  // Ranije je obećavalo „tipove koji još nemaju obrazac", a lista je dolazila
  // samo iz njih — kod kupca kome svi tipovi imaju obrazac polje je bilo prazno
  // i čitalo se kao pokvaren padajući spisak. Sada nudi tipove koje mašine
  // zaista koriste, a druga rečenica kaže da spisak nije obavezujući.
  'admin.templates.field.type.hint':
    'Obrazac se primjenjuje na sve radne centre ovog tipa. Ponuđeni su tipovi koje vaše mašine već koriste; možete upisati i tip koji još ne postoji.',
  'admin.templates.field.type.hint.empty':
    'Obrazac se primjenjuje na sve radne centre ovog tipa. Upišite tip onako kako ga zovete u pogonu.',
  'admin.templates.field.type.explain':
    'Tip povezuje obrazac s radnim centrima: svaki radni centar ovog tipa ocjenjuje se po ovom obrascu. Tip ne morate pamtiti — kad pravite radni centar, obrazac birate po nazivu.',
  'admin.templates.field.type.option.gap.one': 'bez obrasca · {count} mašina',
  'admin.templates.field.type.option.gap.few': 'bez obrasca · {count} mašine',
  'admin.templates.field.type.option.gap.many': 'bez obrasca · {count} mašina',
  'admin.templates.field.type.option.covered': 'već ima aktivan obrazac',
  'admin.templates.field.scale': 'Maksimalna ocjena',
  'admin.templates.field.scale.hint': 'Obično 5 — toliko stepeni ocjene ima svaki kriterijum.',
  // The way through, at the field itself. `update()` refuses a new scale on a
  // used template outright and the API's own message already names „Nova
  // verzija"; a greyed box that says less than the error it prevents is what
  // the customer reported as „ne može se promijeniti".
  'admin.templates.field.scale.locked':
    'Skala se ne može mijenjati jer su po ovom obrascu već predate evaluacije — svaki izračunat procenat je računat po njoj. Napravite novu verziju dugmetom „Nova verzija" na vrhu kartice i u njoj upišite novu skalu.',
  'admin.templates.field.notes': 'Napomena',
  'admin.templates.field.notes.hint':
    'Bilješka za administratore — ocjenjivač je ne vidi na obrascu.',
  'admin.templates.field.active': 'Obrazac je u upotrebi',
  'admin.templates.field.active.hint':
    'Ako ga isključite, radni centri ovog tipa ostaju bez obrasca i njihove evaluacije se ne mogu raditi dok ne napravite drugi.',
  // The choice and the reason it is final. `update()` refuses a new scale_max
  // on a used template outright, because every stored score_percentage was
  // computed against the old one.
  'admin.templates.field.scale.explain':
    'Skala 1–5 daje ocjenjivaču pet stepeni i finiju sliku stanja, a 1–3 se brže popunjava na terenu; broj se zaključava nakon prve predate evaluacije, jer je svaki već izračunat procenat računat po njemu.',
  'admin.plants.delete.confirm': 'Obrisati pogon „{name}"?',
  'admin.plants.delete.warning': 'Trajno se brišu i svi radni centri u ovom pogonu ({count}), zajedno sa njihovim prijavama, 5S evaluacijama i prilozima. Radnici ostaju, samo bez pogona.',
  'admin.plants.delete.action': 'Obriši sve',
  'admin.badgeSheet.inactive.why': 'Nalog je deaktiviran — kartica za njega ne bi radila.',

  // The company's own settings. Currency is the first one; the warning carries
  // a count because "47 iznosa" is a sentence somebody weighs and "ovo može
  // uticati na podatke" is one they click past.
  // Above the card, before anything is picked. The amber block underneath
  // appears only once something has been changed and carries the count; this
  // is the rule itself, which somebody needs while they are still deciding
  // whether to open the dropdown at all.
  'admin.settings.help':
    'Valuta je jedna za cijelu firmu: mijenja oznaku uz svaki iznos — na ekranu, u PDF izvještaju, u tabeli i u obavještenjima. Brojevi se pri tome ne preračunavaju: 500 ostaje 500 i samo dobija novu oznaku, jer aplikacija nema kurs, a izmišljen kurs bio bi gori od promjene oznake.',
  'admin.settings.currency.title': 'Valuta',
  // Beside the label, because it is the only thing on this screen that is
  // recoverable afterwards: `update()` logs both values with the causer, so
  // the day a report looks wrong there is an answer to when it changed.
  'admin.settings.currency.explain':
    'Promjena se upisuje u dnevnik izmjena sa starom i novom valutom, pa se kasnije zna od kada su iznosi u drugoj valuti.',
  'admin.settings.currency.hint':
    'U čemu firma računa uštede, troškove i nagrade. Vrijedi za sve — za ekran, za PDF, za tabelu i za obavještenja.',
  'admin.settings.currency.label': 'Valuta firme',
  /*
   * Three forms, because 1, 3 and 5 take different ones — "1 iznos",
   * "3 iznosa", "5 iznosa" — and the count here is genuinely often 1 on a
   * customer who has just started. See `pluralForm` in lib/utils.ts.
   */
  'admin.settings.currency.warning.one':
    'Već je upisan {count} iznos u trenutnoj valuti. Nakon promjene će se prikazivati u novoj.',
  'admin.settings.currency.warning.few':
    'Već su upisana {count} iznosa u trenutnoj valuti. Nakon promjene će se prikazivati u novoj.',
  'admin.settings.currency.warning.many':
    'Već je upisano {count} iznosa u trenutnoj valuti. Nakon promjene će se prikazivati u novoj.',
  'admin.settings.currency.warningEmpty':
    'Za sada nema upisanih iznosa, pa je ovo najbolji trenutak za promjenu.',
  'admin.settings.currency.noConversion':
    'Brojevi se ne preračunavaju — mijenja se samo oznaka valute pored njih.',
  'admin.settings.currency.saved': 'Valuta je promijenjena.',

  // ---- Tooltips across Administracija ----
  // One or two sentences behind a question mark, each saying what its label
  // cannot. Every behaviour claimed here was read out of kapro-api first; the
  // comment beside each placement in pages/admin names where.

  // Users. `field.active.synced` is a visible note, not a tooltip: for a
  // KaproMES person the checkbox is not the last word, because the sync
  // rewrites `is_active` from their MES status on every run.
  'admin.users.toolbar.explain':
    '„Aktivacioni kodovi" izdaje i štampa jednokratne kodove za sve koji još nemaju svoju lozinku; „Kartice" izdaje i štampa QR kartice kojima se radnici prijavljuju skeniranjem.',
  'admin.users.column.role.explain':
    'Kod deaktiviranog naloga ovdje piše „Neaktivan" umjesto uloge. Uloga ostaje sačuvana i vraća se čim nalog ponovo aktivirate.',
  'admin.users.column.lastLogin.explain':
    'Upisuje se pri svakoj prijavi — lozinkom, aktivacionim kodom ili karticom. „Nikad" znači da se na nalog još niko nije prijavio; obično osoba još nije dobila ili iskoristila aktivacioni kod.',
  'admin.users.field.plant.explain':
    'Pogon je samo oznaka gdje je osoba zavedena — vidi se na njenom profilu i u broju korisnika na „Pogoni", ali ne određuje na kojim radnim centrima radi. Sinhronizacija iz KaproMES-a upisuje ga samo kad je prazan.',
  'admin.users.field.email.explain':
    'Obavještenja stižu na nju samo ako je uključeno „Šalji obavještenja i na e-poštu". Zaboravljenu lozinku korisnik može sam vratiti preko e-pošte tek kad adresu potvrdi na svom profilu.',
  'admin.users.field.active.synced':
    'Ovaj nalog održava KaproMES i svaka sinhronizacija mu postavlja aktivnost prema statusu u KaproMES-u — ako je osoba tamo i dalje aktivna, deaktivacija ovdje traje samo do sljedeće sinhronizacije.',
  // The body of the delete confirmation. `destroy` soft-deletes and the
  // unique index on `employee_code` still counts the deleted row.
  'admin.users.delete.codeKept':
    'Šifra zaposlenog {code} ostaje zauzeta i nakon brisanja — novi nalog je ne može dobiti.',
  // `issueActivationCode()` ends sessions but never touches the badge token.
  'admin.users.reset.badgeStays':
    'Novi kod ne dira QR karticu: ona i dalje prijavljuje ovog korisnika. Ako je kartica izgubljena ili fotografisana, poništite i nju dugmetom s karticom u njegovom redu.',
  'admin.users.badge.revoke.explain':
    '„Poništi" odmah gasi karticu i ne izdaje novu — radnik se dalje prijavljuje šifrom i lozinkom. Za zamjenu izgubljene kartice koristite „Izdaj novu karticu".',
  'admin.users.badge.closeUnprinted':
    'Zatvorite li prozor prije štampe, kartica ostaje izdata, ali se više ne može prikazati — tada izdajte novu.',

  // Plants. The sticker sheet prints the plant's *name*, not its code.
  'admin.plants.field.name.explain':
    'Naziv pogona štampa se na QR naljepnicama njegovih radnih centara. Naljepnice koje su već zalijepljene rade i nakon promjene, samo nose stari naziv.',
  'admin.plants.workCenterCount.explain':
    'Broje se i aktivni i deaktivirani radni centri — brisanjem pogona odlaze svi.',
  'admin.plants.userCount.explain':
    'Korisnici kojima je ovaj pogon upisan na „Korisnici", i aktivni i deaktivirani. Brisanjem pogona nalozi ostaju, samo bez pogona.',

  // Shifts.
  'admin.shifts.subtitle.explain':
    'Sa definisanim smjenama isti radni centar ima jednu 5S evaluaciju dnevno za svaku smjenu; bez smjena — jednu dnevno.',
  'admin.shifts.field.name.explain':
    'Ovaj naziv evaluator bira na 5S obrascu i njime su označeni grafikoni po smjenama. Preimenovanje se vidi i na ranijim evaluacijama — naziv se ne pamti posebno uz svaku.',
  'admin.shifts.field.endsAt.explain':
    'Upišite oba vremena ili nijedno — samo početak ili samo kraj se ne može sačuvati. Isti početak i kraj ne pokrivaju nijedan trenutak, pa takvu smjenu obrazac nikad ne ponudi sam.',
  'admin.shifts.field.sortOrder.explain':
    'Tim redom se smjene nude na 5S obrascu i ređaju na grafikonima. Ako se vremena dvije smjene preklapaju, obrazac sam ponudi onu koja je ovdje prva.',

  // Company settings. The count is rows, not amounts — see
  // `howMuchIsAlreadyWrittenDown()`.
  'admin.settings.currency.recorded.explain':
    'Broji se svaki prijedlog s upisanom očekivanom ili stvarnom uštedom ili troškom — jednom, koliko god polja imao — i svaka dodijeljena nagrada.',
  'admin.settings.currency.undo.explain':
    'Zato se promjena može i vratiti: izaberite staru valutu i iznosi opet dobiju staru oznaku. Isto se tada dešava i iznosima upisanim u međuvremenu, iako su upisani u novoj.',

  // MES integration. Test and sync read the saved settings, not the form.
  'admin.mes.active.explain':
    'Sinhronizacija postavlja i aktivnost. Radnik je aktivan dok je aktivan u KaproMES-u — deaktivacija ovdje traje samo do sljedeće sinhronizacije. Radni centar iz KaproMES-a ovdje se ne može ni deaktivirati: aktivan je dok ga KaproMES vraća.',
  'admin.mes.save.explain':
    '„Testiraj vezu" i „Sinhronizuj sada" rade sa sačuvanim podacima. Nakon izmjene adrese ili tokena prvo pritisnite „Sačuvaj", pa tek onda testirajte.',

  // The gaps panel above the templates.
  'admin.templates.gaps.body.explain':
    'Broje se samo aktivni radni centri. Mašina koja ima vlastiti obrazac se ne broji, iako njen tip nema obrazac.',
  'admin.templates.gaps.source.explain':
    'Broj u zagradi je broj kriterijuma koji se kopiraju. Kopija dobija istu skalu ocjena i naziv „5S — tip", koji poslije možete promijeniti.',

  // Activity log. Search reads `description` only, and the automatic entries
  // carry a bare event name there — hence the first one.
  'admin.log.search.explain':
    'Pretraga gleda samo tekst zapisa, a on je uvijek na bosanskom — npr. šifra zaposlenog ili „Obrisan". Izmjene prijava i evaluacija tako se ne nalaze; za njih koristite filtere ispod.',
  'admin.log.filter.event.explain':
    'Vrstu izmjene imaju samo automatski zapisi o prijavama i 5S evaluacijama. Čim je izaberete, nestaju zapisi administratorskih ekrana — izdati kodovi, kartice, obrisani korisnici i slično.',
  'admin.log.filter.logName.explain':
    'Dio aplikacije koji je upisao zapis, pod internim nazivom: user su korisnici, work_center radni centri, five_s 5S evaluacije, submission prijave, mes sinhronizacija sa KaproMES-om.',
  'admin.log.causer.system.explain':
    'Zapis bez korisnika iza sebe: upisao ga je automatski posao, npr. noćna sinhronizacija sa KaproMES-om — ili je korisnik koji ga je napravio u međuvremenu obrisan.',
  'admin.log.impersonated.explain':
    'Izmjenu je napravila podrška KaProLean-a prijavljena kao ovaj korisnik: ime ispred je nalog koji je korišten, a ovdje piše ko je zaista radio.',

  // The two print sheets.
  'admin.badgeSheet.intro.explain':
    'Na spisku su samo radnici — karticu za nekog drugog izdajete pojedinačno iz liste korisnika. Unaprijed su označeni aktivni radnici koji još nemaju karticu.',
  'admin.badgeSheet.closeUnprinted':
    'Zatvorite li prozor prije štampe, kartice ostaju izdate, ali se više ne mogu prikazati. Tada ih izdajte ponovo — ovdje ih označite ručno, jer ih spisak više ne označava sam.',
  'admin.activation.intro.explain':
    'Na spisku su aktivni nalozi bez vlastite lozinke: novi, preuzeti iz KaproMES-a i oni kojima je pristup vraćen kodom. Ko je već postavio svoju lozinku, ne dobija kod i lozinka mu ostaje.',
  // A second run here reissues to everybody waiting, which is the wrong answer
  // to one lost slip — the key button on the user's row is the right one.
  'admin.activation.lostSlip':
    'Izgubljen listić otvara taj nalog dok ga neko ne iskoristi — tom radniku odmah izdajte novi kod dugmetom s ključem u njegovom redu. Ne pokrećite ovo izdavanje ponovo: poništilo bi listiće svih koji još čekaju.',

  // The company requires a second factor of administrators and this one has
  // none. The gate names the app to install, because "dvofaktorska zaštita"
  // alone tells nobody what they need in their hand to get past it.
  'admin.twoFactorRequired.title': 'Potrebna je dvofaktorska zaštita',
  'admin.twoFactorRequired.body':
    'Vaša firma traži da svaki administrator ima uključenu dvofaktorsku zaštitu. Dok je ne uključite, administracija je zaključana — ostatak aplikacije radi kao i obično.',
  'admin.twoFactorRequired.how':
    'Treba vam aplikacija za autentifikaciju na telefonu, npr. Google Authenticator ili Microsoft Authenticator. Podešavanje traje oko minut.',
  'admin.twoFactorRequired.action': 'Uključi na profilu',
} as const

export type AdminSlice = Record<keyof typeof admin, string>
